/**
 * MCP 服务器管理工具
 * 管理 Model Context Protocol 服务器的启动和配置
 */

import { spawn, ChildProcess } from 'child_process';
import path from 'path';
import { fileURLToPath } from 'url';
import fs from 'fs-extra';
import chalk from 'chalk';
import ora from 'ora';
import { logger } from './logger.js';

const __dirname = path.dirname(fileURLToPath(import.meta.url));

export interface MCPServerOptions {
  mode: 'stdio' | 'sse';
  port?: number;
  workspace?: string;
}

export interface MCPServerProcess {
  mode: string;
  process: ChildProcess;
  port?: number;
  workspace: string;
}

/**
 * 查找 MCP 服务器包
 */
async function findMCPServerPath(): Promise<string | null> {
  const possiblePaths = [
    // 在 monorepo 中
    path.resolve(__dirname, '../../../../packages/mcp-server'),
    path.resolve(__dirname, '../../../../packages/@gongfu/mcp-server'),
    // 作为依赖安装
    path.resolve(__dirname, '../../../@gongfu/mcp-server'),
    path.resolve(__dirname, '../../../mcp-server'),
    // 在当前项目中
    path.resolve(process.cwd(), 'packages/mcp-server'),
    path.resolve(process.cwd(), 'mcp-server'),
  ];
  
  for (const mcpPath of possiblePaths) {
    if (await fs.pathExists(mcpPath)) {
      const packageJsonPath = path.join(mcpPath, 'package.json');
      if (await fs.pathExists(packageJsonPath)) {
        return mcpPath;
      }
    }
  }
  
  return null;
}

/**
 * 检查 MCP SSE 服务器是否运行
 */
export async function isMCPServerRunning(port: number): Promise<boolean> {
  try {
    const response = await fetch(`http://localhost:${port}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * 启动 MCP 服务器（STDIO 模式）
 */
export async function startMCPServerStdio(options: MCPServerOptions): Promise<MCPServerProcess> {
  const spinner = ora('Starting MCP server (STDIO mode)...').start();
  
  try {
    const mcpPath = await findMCPServerPath();
    
    if (!mcpPath) {
      spinner.fail('MCP server package not found');
      throw new Error(
        'Could not find MCP server package. Please ensure it is available in your project.'
      );
    }
    
    spinner.text = `Found MCP server at ${path.relative(process.cwd(), mcpPath)}`;
    
    // 检查是否已构建
    const distPath = path.join(mcpPath, 'dist');
    if (!await fs.pathExists(distPath)) {
      spinner.text = 'Building MCP server...';
      await runCommand('pnpm', ['build'], mcpPath);
    }
    
    // 设置环境变量
    const env = {
      ...process.env,
      WORKFLOW_WORKSPACE: path.resolve(options.workspace || process.cwd()),
      NODE_ENV: 'production',
    };
    
    // 启动 STDIO 服务器
    const startScript = path.join(distPath, 'start-stdio.js');
    const mcpProcess = spawn('node', [startScript], {
      env,
      stdio: 'inherit',
    });
    
    spinner.succeed('MCP server started (STDIO mode)');
    
    return {
      mode: 'stdio',
      process: mcpProcess,
      workspace: env.WORKFLOW_WORKSPACE,
    };
  } catch (error) {
    spinner.fail('Failed to start MCP server');
    throw error;
  }
}

/**
 * 启动 MCP 服务器（SSE 模式）
 */
export async function startMCPServerSSE(options: MCPServerOptions): Promise<MCPServerProcess> {
  const spinner = ora('Starting MCP server (SSE mode)...').start();
  
  try {
    const mcpPath = await findMCPServerPath();
    
    if (!mcpPath) {
      spinner.fail('MCP server package not found');
      throw new Error(
        'Could not find MCP server package. Please ensure it is available in your project.'
      );
    }
    
    spinner.text = `Found MCP server at ${path.relative(process.cwd(), mcpPath)}`;
    
    // 检查是否已构建
    const distPath = path.join(mcpPath, 'dist');
    if (!await fs.pathExists(distPath)) {
      spinner.text = 'Building MCP server...';
      await runCommand('pnpm', ['build'], mcpPath);
    }
    
    const port = options.port || 3457;
    
    // 检查端口
    if (await isMCPServerRunning(port)) {
      spinner.info(`MCP SSE server already running on port ${port}`);
      return {
        mode: 'sse',
        process: null as any,
        port,
        workspace: path.resolve(options.workspace || process.cwd()),
      };
    }
    
    // 设置环境变量
    const env = {
      ...process.env,
      PORT: port.toString(),
      WORKFLOW_WORKSPACE: path.resolve(options.workspace || process.cwd()),
      NODE_ENV: 'production',
    };
    
    // 启动 SSE 服务器
    const startScript = path.join(distPath, 'start-sse.js');
    const mcpProcess = spawn('node', [startScript], {
      env,
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    
    // 等待服务器就绪
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        mcpProcess.kill();
        spinner.fail('MCP server startup timeout');
        reject(new Error('MCP server startup timeout'));
      }, 30000); // 30秒超时
      
      let isReady = false;
      
      const checkServer = setInterval(async () => {
        if (await isMCPServerRunning(port)) {
          if (!isReady) {
            isReady = true;
            clearInterval(checkServer);
            clearTimeout(timeout);
            spinner.succeed(`MCP server started (SSE mode) on port ${port}`);
            resolve();
          }
        }
      }, 500);
      
      // 监听输出
      mcpProcess.stdout?.on('data', (data) => {
        const output = data.toString();
        if (process.env.DEBUG) {
          console.log(chalk.gray(`[MCP] ${output.trim()}`));
        }
      });
      
      mcpProcess.stderr?.on('data', (data) => {
        const error = data.toString();
        console.error(chalk.red(`[MCP Error] ${error.trim()}`));
      });
      
      mcpProcess.on('error', (error) => {
        clearInterval(checkServer);
        clearTimeout(timeout);
        reject(error);
      });
    });
    
    return {
      mode: 'sse',
      process: mcpProcess,
      port,
      workspace: env.WORKFLOW_WORKSPACE,
    };
  } catch (error) {
    spinner.fail('Failed to start MCP server');
    throw error;
  }
}

/**
 * 生成 Claude Desktop 配置
 */
export function generateClaudeConfig(workspace: string): string {
  return JSON.stringify({
    mcpServers: {
      workflow: {
        command: 'workflow',
        args: ['mcp', '--mode', 'stdio'],
        env: {
          WORKFLOW_WORKSPACE: workspace,
        },
      },
    },
  }, null, 2);
}

/**
 * 测试 MCP 连接
 */
export async function testMCPConnection(url: string): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      logger.info('MCP server status:', data);
      return true;
    }
    return false;
  } catch (error) {
    logger.error('MCP connection test failed:', error);
    return false;
  }
}

/**
 * 运行命令
 */
async function runCommand(command: string, args: string[], cwd: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      cwd,
      stdio: 'inherit',
    });
    
    child.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`${command} ${args.join(' ')} failed with code ${code}`));
      }
    });
    
    child.on('error', reject);
  });
}